import { PaginaNode } from '@gql/graphql'
import { GenericHero, GenericSlider, Page, Prefooter } from 'components/organisms'
import { useTrans } from 'hooks'
import { useState } from 'react'
import { A11y } from 'swiper/modules'
import { SwiperSlide } from 'swiper/react'
import { mockSearchResult } from '../../../utils/mock'
import { Chip, DownloadLink, WContainer } from '../../atoms'
import {
  ArticoloCard,
  CardCollezione,
  PaginaCard,
  ProdottoCard,
  ProgettoCard,
} from '../../molecules'
import styles from './SearchResultTemplate.module.sass'
import { useRouter } from 'next/router'

interface Props {
  pagina?: PaginaNode
  query?: string
  risultati?: any[]
}

const SearchResultTemplate = (props: Props) => {
  const { pagina = undefined, query = '', risultati = [] } = props

  const tipologie = [
    { type: 'prodotto', name: 'Collezioni' },
    { type: 'colore', name: 'Colori' },
    { type: 'documento', name: 'Download' },
    { type: 'certificazione', name: 'Certificazioni' },
    { type: 'progetto', name: 'Progetti' },
    { type: 'magazine', name: 'Dal Magazine' },
    { type: 'pagina', name: 'Pagine' },
  ]

  const listings = tipologie
    .map(({ type, name }) => ({
      name,
      items: risultati.filter((x) => x.tipologia === type),
    }))
    .filter((listing) => listing.items.length > 0)

  const t = useTrans()
  const [selectedChip, setSelectedChip] = useState(listings.length > 0 ? listings[0].name : '')
  // let filteredResults = useMemo(() => items.find((x) => x.name === selectedChip), [selectedChip])
  const currentListing = listings.find((x) => x.name === selectedChip)
  const resultsCount = currentListing?.items.length ?? 0
  const ListingRenderedElement = (i, key) => {
    switch (selectedChip) {
      case 'Collezioni':
        return (
          <ProdottoCard
            item={{
              title: i.title,
              quickShip: i.quickship === 1,
              novita: i.novita === 1,
              url: i.url,
              listingImage: { url: i.image },
            }}
            key={key}
          />
        )
      case 'Colori':
        return (
          <CardCollezione
            item={{ nome: i.title, url: i.url, image: { url: i.image }, finitura: i.collezione }}
            variant="colori"
            key={key}
          />
        )
      case 'Download':
        return <DownloadLink item={{ name: i.titolo, url: i.url }} key={key} />
      case 'Certificazioni':
        return <DownloadLink item={{ name: i.titolo, url: i.url }} key={key} />
      case 'Progetti':
        return (
          <ProgettoCard
            item={{
              title: i.title,
              url: i.url,
              imageUrl: i.image,
              destinazioni: [{ titolo: i.tag }],
              premio: i.award_icon,
            }}
            key={key}
          />
        )
      case 'Dal Magazine':
        return (
          <ArticoloCard
            news={{
              title: i.titolo,
              imageUrl: i.image,
              premio: i.award_icon,
              ...i,
            }}
            key={key}
          />
        )
      case 'Pagine':
        return <PaginaCard pagina={i} key={key} />
      default:
        return <ProdottoCard item={i} key={key} />
    }
  }

  const getCanonical = (): string => {
    if (typeof window === 'undefined') return ''
    return `${window.location.origin}${window.location.pathname}`
  }

  return (
    <Page
      pagina={{ ...pagina, seo: { ...pagina.seo, canonicalUrl: getCanonical() } }}
      className={`${styles.root} ${styles[`root--listing${selectedChip.replace(/ /g, '')}`]}`}
    >
      <main>
        <GenericHero pagina={pagina} />
        <WContainer>
          <div className={styles.content}>
            <div className={styles.slider}>
              <GenericSlider
                conf={{
                  modules: [A11y],
                  spaceBetween: 8,
                  slidesPerView: 'auto',
                  breakpoints: {
                    1024: {
                      spaceBetween: 16,
                    },
                  },
                }}
              >
                {listings.map((l, indx) => (
                  <SwiperSlide key={indx}>
                    <Chip
                      key={l.name}
                      label={`${t(l.name)} (${l.items.length})`}
                      selected={selectedChip === l.name}
                      onClick={() => setSelectedChip(l.name)}
                    />
                  </SwiperSlide>
                ))}
              </GenericSlider>
            </div>

            <p className={styles.resultsLabel}>
              {resultsCount > 0 ? (
                <>
                  {resultsCount} {resultsCount === 1 ? t('Risultato') : t('Risultati')}
                </>
              ) : (
                t('Nessun risultato trovato')
              )}
            </p>

            <h3 className={styles.title}>{t(`${selectedChip}`)}</h3>
            <div className={styles.listingContainer}>
              {listings
                .find((x) => x.name === selectedChip)
                ?.items.map((i, indx) => ListingRenderedElement(i, indx))}
            </div>
          </div>
        </WContainer>
        <Prefooter />
      </main>
    </Page>
  )
}

export default SearchResultTemplate
